import React, { useMemo, useState, useEffect } from 'react';
import { Select } from 'antd';
import { useGetSelectFieldValues } from '@/shared/api/client/field-configuration-controller';
import type { SelectFieldOption } from '@/shared/api/model';

export interface GroupSelectProps {
  disabled?: boolean;
  placeholder?: string;
  value?: string;
  onChange?: (value: string | undefined) => void;
  onBlur?: () => void;
  form?: any;
  branchFieldName?: string;
}

/**
 * Group select component.
 * Fetches customer groups from the 'groups' provider.
 * Supports filtering by branch.
 *
 * Used for parameter type: GROUP_SELECT
 */
const GroupSelect: React.FC<GroupSelectProps> = ({
  disabled,
  placeholder,
  form,
  branchFieldName = 'branch',
  ...rest
}) => {
  const [search, setSearch] = useState<string | undefined>(undefined);

  // Watch branch field for filtering
  const branchValue = form?.getFieldValue(branchFieldName);

  const params = useMemo(() => ({
    search,
    fieldName: 'groups',
    entityType: 'REPORT',
    branch: branchValue && branchValue !== '-1' ? branchValue : undefined,
  }), [search, branchValue]);

  const { data: options, isLoading, error, refetch } = useGetSelectFieldValues('groups', params as any, {
    query: {
      staleTime: 5 * 60 * 1000,
    },
  });

  // Refetch when branch changes
  useEffect(() => {
    refetch();
  }, [branchValue, refetch]);

  const selectOptions = useMemo(() => {
    const list: SelectFieldOption[] = Array.isArray(options) ? options : [];
    return list.map((opt) => ({
      value: opt.value,
      label: opt.label,
      disabled: opt.disabled,
    }));
  }, [options]);

  return (
    <Select
      showSearch
      allowClear
      disabled={disabled}
      loading={isLoading}
      placeholder={placeholder || 'Select group'}
      options={selectOptions}
      filterOption={false}
      onSearch={(q) => setSearch(q)}
      notFoundContent={error ? 'Failed to load groups' : isLoading ? 'Loading...' : 'No groups'}
      optionFilterProp="label"
      {...rest}
    />
  );
};

export default GroupSelect;
